/**
 * Product Carousel Modal - Custom Web Component
 * A modern carousel modal for product media with thumbnail navigation
 */

if (!customElements.get("product-carousel-modal")) {
  customElements.define(
    "product-carousel-modal",
    class ProductCarouselModal extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.totalSlides = 0;
        this.isOpen = false;
        this.openedBy = null;

        // Bind methods
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
      }

      connectedCallback() {
        this.slides = this.querySelectorAll(".carousel-modal__slide");
        this.thumbs = this.querySelectorAll(".carousel-modal__thumb");
        this.totalSlides = this.slides.length;
        this.currentSlideEl = this.querySelector("[data-current-slide]");
        this.viewport = this.querySelector(".carousel-modal__viewport");

        // Close button and overlay
        this.querySelectorAll("[data-carousel-close]").forEach((el) => {
          el.addEventListener("click", () => this.hide());
        });

        // Navigation arrows
        const prevBtn = this.querySelector("[data-carousel-prev]");
        const nextBtn = this.querySelector("[data-carousel-next]");

        if (prevBtn) prevBtn.addEventListener("click", () => this.prevSlide());
        if (nextBtn) nextBtn.addEventListener("click", () => this.nextSlide());

        // Thumbnail clicks
        this.thumbs.forEach((thumb, index) => {
          thumb.addEventListener("click", () => this.goToSlide(index));
        });

        // Touch events for swipe
        if (this.viewport) {
          this.viewport.addEventListener("touchstart", this.handleTouchStart, {
            passive: true,
          });
          this.viewport.addEventListener("touchmove", this.handleTouchMove, {
            passive: false,
          });
          this.viewport.addEventListener("touchend", this.handleTouchEnd, {
            passive: true,
          });
        }
      }

      show(opener, mediaId = null) {
        this.openedBy = opener;
        this.isOpen = true;

        // Find the slide to show based on media ID
        let slideIndex = 0;
        if (mediaId) {
          // Extract just the numeric ID if it contains a section prefix (e.g., "section-id-12345" -> "12345")
          const numericId = mediaId.toString().split('-').pop();
          
          this.slides.forEach((slide, index) => {
            const slideMediaId = slide.dataset.mediaId;
            // Match either exact ID or numeric portion
            if (slideMediaId === mediaId || slideMediaId === numericId) {
              slideIndex = index;
            }
          });
        }

        // Preload all images immediately for fast navigation
        this.preloadAllImages();

        // Show modal
        this.setAttribute("aria-hidden", "false");
        this.classList.add("is-open");
        document.body.classList.add("overflow-hidden");

        // Go to the requested slide
        this.goToSlide(slideIndex);

        // Add keyboard listener
        document.addEventListener("keydown", this.handleKeydown);

        // Focus trap
        this.focus();
      }

      preloadAllImages() {
        // Force load all images in the carousel
        this.querySelectorAll(".carousel-modal__image").forEach((img) => {
          if (!img.complete) {
            img.loading = "eager";
            // Create a new Image to trigger load
            const preloader = new Image();
            preloader.src = img.src;
          }
        });
      }

      hide() {
        this.isOpen = false;
        this.setAttribute("aria-hidden", "true");
        this.classList.remove("is-open");
        document.body.classList.remove("overflow-hidden");

        // Remove keyboard listener
        document.removeEventListener("keydown", this.handleKeydown);

        // Pause any videos
        this.querySelectorAll("video").forEach((video) => video.pause());

        // Return focus to opener
        if (this.openedBy) {
          this.openedBy.focus();
        }
      }

      goToSlide(index) {
        // Clamp index
        if (index < 0) index = this.totalSlides - 1;
        if (index >= this.totalSlides) index = 0;

        this.currentIndex = index;

        // Update slides visibility
        this.slides.forEach((slide, i) => {
          slide.classList.toggle("is-active", i === index);
        });

        // Update thumbnails
        this.thumbs.forEach((thumb, i) => {
          thumb.classList.toggle("is-active", i === index);
        });

        // Scroll active thumbnail into view
        if (this.thumbs[index]) {
          this.thumbs[index].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }

        // Update counter
        if (this.currentSlideEl) {
          this.currentSlideEl.textContent = index + 1;
        }
      }

      nextSlide() {
        this.goToSlide(this.currentIndex + 1);
      }

      prevSlide() {
        this.goToSlide(this.currentIndex - 1);
      }

      handleKeydown(event) {
        switch (event.key) {
          case "Escape":
            this.hide();
            break;
          case "ArrowLeft":
            this.prevSlide();
            break;
          case "ArrowRight":
            this.nextSlide();
            break;
        }
      }

      // Touch/Swipe handling
      handleTouchStart(event) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }

      handleTouchMove(event) {
        if (!this.touchStartX) return;

        const deltaX = event.touches[0].clientX - this.touchStartX;
        const deltaY = event.touches[0].clientY - this.touchStartY;

        // If horizontal swipe is dominant, prevent scrolling
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          event.preventDefault();
        }
      }

      handleTouchEnd(event) {
        if (!this.touchStartX) return;

        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const deltaY = event.changedTouches[0].clientY - this.touchStartY;
        const threshold = 50;

        // Mobile escape hatch: swipe down to close the full-screen gallery.
        // This protects shoppers if the fixed close button is obscured by browser UI.
        if (deltaY > 90 && Math.abs(deltaY) > Math.abs(deltaX)) {
          this.hide();
        } else if (deltaX > threshold) {
          this.prevSlide();
        } else if (deltaX < -threshold) {
          this.nextSlide();
        }

        this.touchStartX = null;
        this.touchStartY = null;
      }
    }
  );
}

// Extend modal opener functionality
document.addEventListener("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest(".product__modal-opener .product__media-toggle");
    if (!toggle) return;

    const modal = toggle.closest("product-info")?.querySelector("product-carousel-modal")
      || document.querySelector("product-carousel-modal");
    if (modal) {
      event.preventDefault();
      modal.show(toggle, toggle.dataset.mediaId);
    }
  });

  // Handle "MORE PHOTOS" overlay button clicks
  document.querySelectorAll(".pmtile__btn").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const modal = document.querySelector("product-carousel-modal");
      if (modal) {
        // Open at slide index 3 (4th image - the one behind the blur overlay)
        modal.show(btn);
        modal.goToSlide(3);
      }
    });
  });
});
